import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from "@velaro/velaro-component-lib";
import * as React from "react";
import {
  BubbleInvitation,
  BannerInvitation,
  PopoverInvitation,
  InlineInvitation,
  Invitation,
  invitationTypeDefs
} from "./types";
import {
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";
import { useState } from "react";
import BaseInvitationEditor from "./BaseInvitationEditor";
import useApi from "@/hooks/useApi";
import AuthService from "@/services/AuthService";
import { ExpressionEditor } from "@/components/Expressions/ExpressionEditor";
import { Expression } from "@/components/Expressions/types";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import { FormGroup } from "../Workflows/RightAside/RightAsideHelpers";
import Input from "@/components/Input";

interface Props {
  onUpdate(invitation: Invitation): void;
  onClose(): void;
  invitation?: Invitation;
}

interface QuickstartTemplate {
  name: string;
  description: string;
  invitation: Record<string, unknown>;
}

const QUICKSTART_TEMPLATES: QuickstartTemplate[] = [
  // ── Engagement ───────────────────────────────────────────────────────────
  {
    name: "Welcome first-time visitors",
    description: "Bubble · 10s on page · first-time visitor",
    invitation: {
      type: "bubble",
      messages: ["Hi there! Welcome — can I help you find something?"],
      messageDelaySeconds: 3,
      triggerExpression: [
        [
          { type: "timeOnPage", data: '{"seconds":10}' },
          { type: "returnVisitor", data: '{"isReturn":false}' }
        ]
      ],
      resetFrequency: "Daily",
      maxFires: 1,
      floatOntoScreen: true,
      startingAnimation: "right-center"
    }
  },
  {
    name: "Return visitor re-engage",
    description: "Bubble · 5s on page · returning visitor",
    invitation: {
      type: "bubble",
      messages: ["Welcome back! Can we help you with anything today?"],
      messageDelaySeconds: 3,
      triggerExpression: [
        [
          { type: "timeOnPage", data: '{"seconds":5}' },
          { type: "returnVisitor", data: '{"isReturn":true}' }
        ]
      ],
      resetFrequency: "Weekly",
      maxFires: 1,
      floatOntoScreen: true,
      startingAnimation: "right-center"
    }
  },

  // ── Exit intent ───────────────────────────────────────────────────────────
  {
    name: "Exit intent — pricing page",
    description: "Popover · exit intent · pricing page",
    invitation: {
      type: "popover",
      messages: [
        "Before you go — have a question about pricing? We'd love to help!"
      ],
      messageDelaySeconds: 0,
      ctaLabel: "Chat with us",
      triggerExpression: [
        [
          { type: "exitIntent", data: '{}' },
          { type: "url", data: '{"comparator":"contains","value":"pricing"}' }
        ]
      ],
      resetFrequency: "Daily",
      maxFires: 1,
      startingAnimation: "bottom-center"
    }
  },
  {
    name: "Exit intent — checkout",
    description: "Popover · exit intent · cart or checkout page",
    invitation: {
      type: "popover",
      messages: [
        "Don't leave yet! Can we answer a question about your order?"
      ],
      messageDelaySeconds: 0,
      ctaLabel: "Get help",
      triggerExpression: [
        [
          { type: "exitIntent", data: '{}' },
          {
            type: "url",
            data: '{"comparator":"contains","value":"checkout"}'
          }
        ]
      ],
      resetFrequency: "Daily",
      maxFires: 1,
      startingAnimation: "bottom-center"
    }
  },

  // ── High-intent pages ────────────────────────────────────────────────────
  {
    name: "Pricing page — 20s dwell",
    description: "Popover · 20s on pricing page",
    invitation: {
      type: "popover",
      messages: ["Comparing plans? We can walk you through the differences."],
      messageDelaySeconds: 0,
      ctaLabel: "Talk to sales",
      triggerExpression: [
        [
          { type: "timeOnPage", data: '{"seconds":20}' },
          {
            type: "url",
            data: '{"comparator":"contains","value":"pricing"}'
          }
        ]
      ],
      resetFrequency: "Daily",
      maxFires: 1,
      startingAnimation: "right-center"
    }
  },
  {
    name: "Product page — scroll 60%",
    description: "Popover · 60% scroll · product pages",
    invitation: {
      type: "popover",
      messages: ["Questions about this product? Ask us — we respond in seconds."],
      messageDelaySeconds: 0,
      ctaLabel: "Ask a question",
      triggerExpression: [
        [
          { type: "scrollDepth", data: '{"percent":60}' },
          {
            type: "url",
            data: '{"comparator":"contains","value":"/product"}'
          }
        ]
      ],
      resetFrequency: "Daily",
      maxFires: 1,
      startingAnimation: "right-center"
    }
  },

  // ── Content / blog ────────────────────────────────────────────────────────
  {
    name: "Blog reader to demo",
    description: "Banner · 75% scroll · blog pages",
    invitation: {
      type: "banner",
      message: "Enjoyed this? See how Velaro can help your support team.",
      position: "bottom",
      ctaLabel: "Get a demo",
      triggerExpression: [
        [
          { type: "scrollDepth", data: '{"percent":75}' },
          {
            type: "url",
            data: '{"comparator":"contains","value":"/blog"}'
          }
        ]
      ],
      resetFrequency: "Weekly",
      maxFires: 1
    }
  },
  {
    name: "Knowledge base assist",
    description: "Bubble · 30s on help/docs pages",
    invitation: {
      type: "bubble",
      messages: [
        "Can't find what you need? Our team can help right now."
      ],
      messageDelaySeconds: 2,
      triggerExpression: [
        [
          { type: "timeOnPage", data: '{"seconds":30}' },
          {
            type: "url",
            data: '{"comparator":"contains","value":"/help"}'
          }
        ]
      ],
      resetFrequency: "Daily",
      maxFires: 1,
      startingAnimation: "left-center"
    }
  },

  // ── Campaign / UTM ────────────────────────────────────────────────────────
  {
    name: "Paid traffic — high intent",
    description: "Popover · UTM campaign visitors · 5s delay",
    invitation: {
      type: "popover",
      messages: [
        "Hi! Looks like you came here from one of our ads — any questions?"
      ],
      messageDelaySeconds: 0,
      ctaLabel: "Chat now",
      triggerExpression: [
        [
          { type: "timeOnPage", data: '{"seconds":5}' },
          {
            type: "utmCampaign",
            data: '{"comparator":"exists"}'
          }
        ]
      ],
      resetFrequency: "Daily",
      maxFires: 1,
      startingAnimation: "right-center"
    }
  },

  // ── E-commerce / support ──────────────────────────────────────────────────
  {
    name: "Order status — self-serve prompt",
    description: "Inline Chat · embedded below order section",
    invitation: {
      type: "inline",
      contentType: "inline-chat",
      placeholderText: "Check your order status...",
      ctaLabel: "Track my order",
      triggerExpression: [[]],
      resetFrequency: "Never",
      maxFires: null,
      allowDuringChat: false
    }
  },
  {
    name: "Post-purchase feedback",
    description: "Banner · confirmation pages",
    invitation: {
      type: "banner",
      message: "How was your experience? Chat with us — we'd love to hear.",
      position: "bottom",
      ctaLabel: "Give feedback",
      triggerExpression: [
        [
          {
            type: "url",
            data: '{"comparator":"contains","value":"confirmation"}'
          }
        ]
      ],
      resetFrequency: "Never",
      maxFires: 1
    }
  },
  {
    name: "Custom HTML skin — full control",
    description: "Popover · custom branded HTML · HTML editor",
    invitation: {
      type: "popover",
      messages: [],
      messageDelaySeconds: 5,
      ctaLabel: "",
      htmlContent:
        '<div class="vi-card">\n  <p class="vi-msg">Hi {VisitorName}! Have a question?</p>\n  <a class="vi-btn" href="#">{Chat}Start chat{EndChat}</a>\n  <button class="vi-close">{Close}✕{EndClose}</button>\n</div>',
      cssContent:
        ".vi-card{background:#fff;border-radius:12px;padding:20px;box-shadow:0 4px 20px rgba(0,0,0,.12);font-family:sans-serif;}\n.vi-msg{margin:0 0 12px;font-size:15px;}\n.vi-btn{display:inline-block;background:#3b82f6;color:#fff;padding:8px 16px;border-radius:6px;text-decoration:none;font-size:13px;}\n.vi-close{float:right;background:none;border:none;font-size:18px;cursor:pointer;color:#888;}",
      triggerExpression: [[{ type: "timeOnPage", data: '{"seconds":8}' }]],
      resetFrequency: "Daily",
      maxFires: 1,
      startingAnimation: "right-center"
    }
  }
];

function getDefaultInvitationModel(): BubbleInvitation {
  return {
    siteId: AuthService.profile.siteId,
    type: "bubble",
    name: "New Invitation",
    description: "Engages visitors with a friendly message",
    triggerExpression: [[{ type: "timeOnPage", data: '{"seconds":10}' }]],
    messageDelaySeconds: 3,
    resetFrequency: "Never",
    maxFires: 1,
    floatOntoScreen: true,
    allowDuringChat: false,
    startingAnimation: "right-center",
    messages: ["Hello! Can I help you today?"]
  };
}

export default function InvitationModal({
  invitation,
  onUpdate,
  onClose
}: Props) {
  const [workingModel, setWorkingModel] = useState<Invitation>(
    invitation || getDefaultInvitationModel()
  );

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [showQuickstarts, setShowQuickstarts] = useState(false);
  const api = useApi();
  const { displayToast } = useToastAlert();
  const SubtypeEditor = invitationTypeDefs.find(
    (t) => t.value === workingModel.type
  )?.editor;

  const isNew = !workingModel.id;
  const buttonLabel = isNew
    ? saving
      ? "Creating"
      : "Create"
    : saving
    ? "Saving"
    : "Save";

  function validate() {
    const errs: Record<string, string> = {};
    if (!workingModel.type) errs["type"] = "Type is required";
    if (!workingModel.name) errs["name"] = "A name is required";

    if (workingModel.type === "bubble") {
      const bubble = workingModel as BubbleInvitation;
      if (!bubble.messages || bubble.messages.length === 0)
        errs["messages"] = "At least one message is required";
      if ((bubble.messageDelaySeconds ?? 0) < 0)
        errs["messageDelaySeconds"] = "Message delay must be a positive number";
    }

    if (workingModel.type === "popover") {
      const popover = workingModel as PopoverInvitation;
      if (!popover.htmlContent && (!Array.isArray(popover.messages) || popover.messages.length === 0))
        errs["messages"] = "At least one message is required (or use a custom HTML skin)";
    }

    if (workingModel.type === "banner") {
      const banner = workingModel as BannerInvitation;
      if (!banner.message) errs["message"] = "A message is required";
    }

    return errs;
  }

  const save = async () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSaving(true);
    const response = await api.messaging.post("Invitations", workingModel);

    if (response.ok) {
      displayToast(Severity.Success, "Invitation saved successfully.");
      onUpdate(await response.json());
    } else {
      const error = await response.json();
      displayToast(Severity.Error, JSON.stringify(error));
    }
    onClose();
    setSaving(false);
  };

  return (
    <Modal show={true} maxWidth="max-w-[1100px]">
      <ModalHeader
        title={isNew ? "Create Invitation" : "Edit Invitation"}
        onClose={onClose}
      />
      <ModalBody>
        <div className="flex gap-4">
          {/* Left column: form */}
          <div className="p-1 w-1/2 max-h-[700px] overflow-auto">
            {/* Quickstart templates */}
            <div className="mb-4 border border-blue-100 rounded-lg bg-blue-50 p-3">
              <button
                type="button"
                onClick={() => setShowQuickstarts((prev) => !prev)}
                className="flex items-center gap-2 w-full text-left text-sm font-medium text-blue-700"
              >
                <span>Quickstart templates ({QUICKSTART_TEMPLATES.length})</span>
                <span className="ml-auto text-xs">
                  {showQuickstarts ? "▲" : "▼"}
                </span>
              </button>
              {showQuickstarts && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {QUICKSTART_TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.name}
                      type="button"
                      onClick={() => {
                        setWorkingModel((prev) => ({
                          ...prev,
                          ...(tpl.invitation as Partial<Invitation>),
                          name: tpl.name,
                          description: tpl.description,
                          siteId: prev.siteId
                        } as Invitation));
                        setShowQuickstarts(false);
                      }}
                      className="text-left text-xs border border-blue-200 bg-white rounded p-2 hover:bg-blue-50"
                    >
                      <div className="font-medium text-gray-800">{tpl.name}</div>
                      <div className="text-gray-500 mt-0.5">{tpl.description}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <BaseInvitationEditor
              invitation={workingModel}
              errors={errors}
              onUpdate={setWorkingModel}
            />

            {/* Department routing */}
            <FormGroup
              label="Route to Department"
              tooltipText="Visitors who accept this invitation will be routed to this team"
            >
              <Input
                type="number"
                value={
                  workingModel.routeToTeamId !== undefined
                    ? String(workingModel.routeToTeamId)
                    : ""
                }
                onChange={(value) =>
                  setWorkingModel((prev) => ({
                    ...prev,
                    routeToTeamId: value ? parseInt(value) : undefined
                  }))
                }
              />
            </FormGroup>

            {SubtypeEditor && (
              <SubtypeEditor
                invitation={workingModel}
                errors={errors}
                onUpdate={setWorkingModel}
              />
            )}
          </div>

          <div className="border border-y border-slate-gray-50"></div>

          {/* Right column: trigger rules */}
          <div className="p-1 w-1/2 max-h-[700px] overflow-auto">
            <div className="mb-2">
              <div className="text-sm font-semibold text-gray-800">Display Rules</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Define when this invitation appears. All conditions in a row must match (AND).
                Any row can trigger (OR). Leave empty to always show.
              </div>
            </div>
            <ExpressionEditor
              expressionType="send-invite"
              expression={workingModel.triggerExpression}
              onUpdate={(triggerExpression: Expression) => {
                setWorkingModel({ ...workingModel, triggerExpression });
              }}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex gap-2 justify-end py-4">
          <SecondaryBlueButton label="Close" onClick={onClose} />
          <PrimaryBlueButton
            disabled={saving}
            label={buttonLabel}
            onClick={save}
          />
        </div>
      </ModalFooter>
    </Modal>
  );
}
